import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, PenLine, Mail, Megaphone, Sparkles, Copy, Trash2, Save,
  Loader2, BookmarkPlus, RefreshCcw, History, BookOpen, HelpCircle, Link2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { api, errorMessage, API_BASE, tokenStore, orgStore } from "@/lib/api";
import { toast } from "sonner";
import { ModuleGuide } from "@/components/ModuleGuide";
import { CONTENT_GUIDE } from "@/lib/guides";
import { SamTipCard } from "@/components/SamTipCard";
import { useSamTip } from "@/context/SamTipsContext";

const GENERATORS = [
  {
    id: "blog", name: "Blog Post Writer",
    icon: FileText, accent: "from-indigo-500/40 to-indigo-500/10 border-indigo-500/20",
    description: "Long-form, SEO-aware, sectioned posts.",
  },
  {
    id: "linkedin", name: "LinkedIn Post Generator",
    icon: PenLine, accent: "from-violet-500/40 to-violet-500/10 border-violet-500/20",
    description: "Scroll-stopping posts that ship engagement.",
  },
  {
    id: "email", name: "Email Copy Generator",
    icon: Mail, accent: "from-fuchsia-500/40 to-fuchsia-500/10 border-fuchsia-500/20",
    description: "Subject + body, merge-tag ready.",
  },
  {
    id: "ad", name: "Ad Copy Generator",
    icon: Megaphone, accent: "from-emerald-500/30 to-emerald-500/10 border-emerald-500/20",
    description: "Headlines + descriptions per platform.",
  },
];

const TONES = ["professional", "casual", "witty", "authoritative", "friendly", "direct", "playful"];

const LABEL = {
  blog: "Blog post", linkedin: "LinkedIn post", email: "Email", ad: "Ad copy",
};

export default function Content() {
  const [tab, setTab] = useState("generators");
  const [activeGen, setActiveGen] = useState("blog");
  const [prefill, setPrefill] = useState(null);
  const [genCount, setGenCount] = useState(null);
  const tip = useSamTip("content");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/content/generations?limit=1");
        const arr = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.rows || []);
        if (!cancelled) setGenCount(arr.length);
      } catch (_) {
        if (!cancelled) setGenCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mx-auto max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="text-eyebrow text-fuchsia-400" data-testid="content-eyebrow">Content engine</div>
        <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">
          Write everything. <span className="brand-text">Faster.</span>
        </h1>
        <p className="mt-2 max-w-2xl text-white/55">
          Four purpose-built generators. One streaming output panel. Save anything to History,
          re-use anything as a Template.
        </p>
      </motion.div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white/5 text-white" data-testid="content-tabs">
          <TabsTrigger value="generators" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-generators">
            Generators
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-history">
            <History className="mr-1 h-3.5 w-3.5" /> History
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-templates">
            <BookOpen className="mr-1 h-3.5 w-3.5" /> Templates
          </TabsTrigger>
          <TabsTrigger value="guide" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-guide">
            <HelpCircle className="mr-1 h-3.5 w-3.5" /> Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generators" className="mt-5">
          <SamTipCard
            show={genCount === 0}
            enabled={tip.enabled}
            message={tip.message}
            prefill={tip.prefill}
            testId="sam-tip-content"
            module="content"
          />
          <GeneratorGrid
            active={activeGen} setActive={setActiveGen}
            prefill={prefill} setPrefill={setPrefill}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-5">
          <HistoryTab onReuse={(g) => { setActiveGen(g.type); setPrefill(g.inputs || {}); setTab("generators"); }} />
        </TabsContent>

        <TabsContent value="templates" className="mt-5">
          <TemplatesTab onUse={(t) => { setActiveGen(t.type); setPrefill(t.inputs || {}); setTab("generators"); }} />
        </TabsContent>
        <TabsContent value="guide" className="mt-5">
          <ModuleGuide
            title={CONTENT_GUIDE.title}
            whatItIs={CONTENT_GUIDE.whatItIs}
            capabilities={CONTENT_GUIDE.capabilities}
            howTo={CONTENT_GUIDE.howTo}
            ctaLabel={CONTENT_GUIDE.cta}
            onCta={() => setTab("generators")}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ GENERATORS ============

function GeneratorGrid({ active, setActive, prefill, setPrefill }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="space-y-3 lg:col-span-3" data-testid="generator-tabs">
        {GENERATORS.map((g) => (
          <button
            key={g.id}
            onClick={() => setActive(g.id)}
            className={`group flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition ${
              active === g.id
                ? "border-fuchsia-500/30 bg-fuchsia-500/5"
                : "border-white/5 bg-white/[0.02] hover:bg-white/[0.05]"
            }`}
            data-testid={`gen-tab-${g.id}`}
          >
            <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br border ${g.accent}`}>
              <g.icon className="h-4 w-4 text-white/95" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-sm font-medium text-white">{g.name}</div>
              <div className="mt-0.5 line-clamp-2 text-xs text-white/45">{g.description}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="lg:col-span-9">
        <GeneratorPanel
          type={active}
          prefill={prefill}
          onConsumePrefill={() => setPrefill(null)}
        />
      </div>
    </div>
  );
}

function GeneratorPanel({ type, prefill, onConsumePrefill }) {
  const [inputs, setInputs] = useState({});
  const [streaming, setStreaming] = useState(false);
  const [output, setOutput] = useState("");
  const [savedGenId, setSavedGenId] = useState(null);
  // Iter 23 (audit D1) — KB grounding state. Identical pattern to Chat.jsx.
  const [kbReadiness, setKbReadiness] = useState({ ready: false, ready_count: 0 });
  const [useKb, setUseKb] = useState(true);
  const [kbSources, setKbSources] = useState([]);  // [{document_id, filename, score}, ...]
  const abortRef = useRef(null);

  useEffect(() => {
    api.get("/kb/readiness")
      .then((r) => setKbReadiness(r.data))
      .catch(() => setKbReadiness({ ready: false, ready_count: 0 }));
  }, []);
  const outputRef = useRef(null);

  // Reset/prefill inputs when type or prefill changes
  useEffect(() => {
    if (prefill) {
      setInputs(prefill);
      onConsumePrefill?.();
    } else {
      setInputs(defaultInputs(type));
    }
    setOutput("");
    setSavedGenId(null);
  }, [type, prefill, onConsumePrefill]);

  // Auto-scroll output
  useEffect(() => {
    if (outputRef.current && streaming) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output, streaming]);

  const generate = async () => {
    if (streaming) return;
    setStreaming(true);
    setOutput("");
    setSavedGenId(null);
    // Iter 23 (audit D1) — reset Sources between generations.
    setKbSources([]);

    const token = tokenStore.get();
    const orgId = orgStore.get();
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (orgId) headers["X-Org-Id"] = orgId;

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch(`${API_BASE}/content/generate`, {
        method: "POST", headers, signal: ctrl.signal,
        // Iter 23 — pass use_kb so the backend retrieves RAG context.
        // When KB isn't ready, the backend silently falls back to no-RAG.
        body: JSON.stringify({ type, inputs, use_kb: useKb && kbReadiness.ready }),
      });
      if (!res.ok || !res.body) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let genId = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6);
          if (!json) continue;
          try {
            const evt = JSON.parse(json);
            if (evt.type === "meta" && evt.generation_id) {
              genId = evt.generation_id;
              // Iter 23 — pick up KB source chips from the meta event.
              if (Array.isArray(evt.kb_chunks)) setKbSources(evt.kb_chunks);
            }
            if (evt.type === "token") setOutput((o) => o + evt.content);
            if (evt.type === "done") setSavedGenId(genId);
            if (evt.type === "error") throw new Error(evt.error || "Generation error");
          } catch (_) { /* ignore malformed */ }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") toast.error(err.message || "Generation failed");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const stop = () => { abortRef.current?.abort(); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(output); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const [templateOpen, setTemplateOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="glass rounded-2xl p-5" data-testid={`generator-panel-${type}`}>
        <div className="grid gap-5 md:grid-cols-2">
          <InputsForm type={type} inputs={inputs} setInputs={setInputs} />
          <OutputPane
            ref={outputRef}
            type={type}
            output={output}
            streaming={streaming}
            onCopy={copy}
            onSaveTemplate={() => setTemplateOpen(true)}
            canSaveTemplate={Object.keys(inputs).length > 0}
            savedGenId={savedGenId}
          />
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-4">
          <div className="text-xs text-white/40">
            {streaming ? "Streaming…" : output ? "Done." : "Fill in the form, then generate."}
          </div>
          <div className="flex gap-2">
            {streaming ? (
              <Button onClick={stop} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="generator-stop">
                Stop
              </Button>
            ) : (
              <>
                {output && (
                  <Button onClick={generate} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="generator-regenerate">
                    <RefreshCcw className="mr-1.5 h-4 w-4" /> Regenerate
                  </Button>
                )}
                <Button onClick={generate} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="generator-generate">
                  <Sparkles className="mr-1.5 h-4 w-4" /> Generate
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Iter 23 (audit D1) — Content Engine KB grounding affordance.
            Mirrors the Chat indicator: switch + status text + empty-state
            CTA. When KB has docs, the toggle defaults ON; the user can
            disable per-generation for purely generic prompts. */}
        <div className="mt-2 flex items-center justify-between text-xs text-white/45">
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="content-rag-toggle-label">
            <Switch
              checked={useKb && kbReadiness.ready}
              onCheckedChange={setUseKb}
              disabled={!kbReadiness.ready}
              data-testid="content-use-kb-toggle"
            />
            <BookOpen className="h-3 w-3 text-fuchsia-400" />
            Use knowledge base
          </label>
          {kbReadiness.ready ? (
            <span data-testid="content-grounded-indicator">
              {useKb
                ? <>Grounded in your KB · <span className="text-white/70">{kbReadiness.ready_count} doc{kbReadiness.ready_count === 1 ? "" : "s"}</span></>
                : "Generic mode — KB off for this generation"}
            </span>
          ) : (
            <Link
              to="/app/knowledge"
              className="text-fuchsia-300 hover:text-fuchsia-200"
              data-testid="content-kb-empty-cta"
            >
              Upload your business docs → make output feel like YOUR business
            </Link>
          )}
        </div>

        {/* Iter 23 — Sources footer chips. Appears underneath the generator
            once a generation completes (or while streaming, once meta lands)
            so the user can see which KB docs grounded the output. */}
        {kbSources.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="content-sources-footer">
            <span className="text-[10px] uppercase tracking-wider text-white/45">Sources</span>
            {kbSources.map((s) => {
              const full = s.filename || s.document_id;
              const label = full.length > 30 ? `${full.slice(0, 28)}…` : full;
              return (
                <Link
                  key={s.document_id}
                  to={`/app/knowledge?doc=${s.document_id}`}
                  title={full}
                  className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[11px] text-white/65 transition hover:bg-white/10 hover:text-white"
                  data-testid={`content-source-chip-${s.document_id}`}
                >
                  <Link2 className="h-2.5 w-2.5 text-fuchsia-300/80" /> {label}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <SaveTemplateDialog
        open={templateOpen} onOpenChange={setTemplateOpen}
        type={type} inputs={inputs}
      />
    </div>
  );
}

function defaultInputs(type) {
  if (type === "blog")     return { topic: "", audience: "", tone: "professional", word_count: 800, keywords: [] };
  if (type === "linkedin") return { topic: "", angle: "insight", tone: "direct", length: "medium" };
  if (type === "email")    return { purpose: "cold_outreach", audience: "", key_points: "", tone: "direct", cta: "book a 15-minute intro call" };
  if (type === "ad")       return { platform: "google", product: "", audience: "", usp: "", tone: "professional" };
  return {};
}

// ---- INPUT FORMS PER TYPE ----

function InputsForm({ type, inputs, setInputs }) {
  const set = (k, v) => setInputs((i) => ({ ...i, [k]: v }));
  if (type === "blog")     return <BlogForm i={inputs} set={set} />;
  if (type === "linkedin") return <LinkedInForm i={inputs} set={set} />;
  if (type === "email")    return <EmailForm i={inputs} set={set} />;
  if (type === "ad")       return <AdForm i={inputs} set={set} />;
  return null;
}

function FieldLabel({ children }) {
  return <Label className="text-xs uppercase tracking-wider text-white/50">{children}</Label>;
}

function BlogForm({ i, set }) {
  const [kwInput, setKwInput] = useState("");
  const addKw = (val) => {
    const v = (val || kwInput).trim();
    if (!v) return;
    if ((i.keywords || []).includes(v)) return;
    set("keywords", [...(i.keywords || []), v]);
    setKwInput("");
  };
  const removeKw = (kw) => set("keywords", (i.keywords || []).filter((k) => k !== kw));
  return (
    <div className="space-y-3" data-testid="blog-form">
      <div className="space-y-1.5">
        <FieldLabel>Topic</FieldLabel>
        <Input value={i.topic || ""} onChange={(e) => set("topic", e.target.value)} placeholder="e.g. How AI is reshaping outbound sales" className="border-white/10 bg-black/40 text-white" data-testid="blog-topic" />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Target audience</FieldLabel>
        <Input value={i.audience || ""} onChange={(e) => set("audience", e.target.value)} placeholder="e.g. VP of Sales at B2B SaaS" className="border-white/10 bg-black/40 text-white" data-testid="blog-audience" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <FieldLabel>Tone</FieldLabel>
          <Select value={i.tone || "professional"} onValueChange={(v) => set("tone", v)}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="blog-tone"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Word count · {i.word_count || 800}</FieldLabel>
          <Slider min={300} max={2000} step={50} value={[i.word_count || 800]} onValueChange={(v) => set("word_count", v[0])} data-testid="blog-wordcount" />
        </div>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Keywords</FieldLabel>
        <div className="flex gap-2">
          <Input
            value={kwInput} onChange={(e) => setKwInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addKw(); } }}
            placeholder="Type and press Enter"
            className="border-white/10 bg-black/40 text-white"
            data-testid="blog-keyword-input"
          />
          <Button type="button" variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10" onClick={() => addKw()}>Add</Button>
        </div>
        {(i.keywords || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="blog-keyword-chips">
            {(i.keywords || []).map((kw) => (
              <button key={kw} onClick={() => removeKw(kw)} className="rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-2 py-0.5 text-[11px] text-fuchsia-200 hover:bg-fuchsia-500/20">
                {kw} <span className="text-fuchsia-300/60">×</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LinkedInForm({ i, set }) {
  return (
    <div className="space-y-3" data-testid="linkedin-form">
      <div className="space-y-1.5">
        <FieldLabel>Topic</FieldLabel>
        <Input value={i.topic || ""} onChange={(e) => set("topic", e.target.value)} placeholder="What's the post about?" className="border-white/10 bg-black/40 text-white" data-testid="li-topic" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <FieldLabel>Angle</FieldLabel>
          <Select value={i.angle || "insight"} onValueChange={(v) => set("angle", v)}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="li-angle"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              <SelectItem value="story">Story</SelectItem>
              <SelectItem value="insight">Insight</SelectItem>
              <SelectItem value="how-to">How-to</SelectItem>
              <SelectItem value="hot-take">Hot take</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Tone</FieldLabel>
          <Select value={i.tone || "direct"} onValueChange={(v) => set("tone", v)}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="li-tone"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Length</FieldLabel>
        <Select value={i.length || "medium"} onValueChange={(v) => set("length", v)}>
          <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="li-length"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
            <SelectItem value="short">Short (60-100 words)</SelectItem>
            <SelectItem value="medium">Medium (120-200 words)</SelectItem>
            <SelectItem value="long">Long (220-320 words)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function EmailForm({ i, set }) {
  return (
    <div className="space-y-3" data-testid="email-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <FieldLabel>Purpose</FieldLabel>
          <Select value={i.purpose || "cold_outreach"} onValueChange={(v) => set("purpose", v)}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="email-purpose"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              <SelectItem value="cold_outreach">Cold outreach</SelectItem>
              <SelectItem value="follow_up">Follow-up</SelectItem>
              <SelectItem value="announcement">Announcement</SelectItem>
              <SelectItem value="newsletter">Newsletter</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Tone</FieldLabel>
          <Select value={i.tone || "direct"} onValueChange={(v) => set("tone", v)}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="email-tone"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Audience</FieldLabel>
        <Input value={i.audience || ""} onChange={(e) => set("audience", e.target.value)} placeholder="e.g. VP of Sales at B2B SaaS" className="border-white/10 bg-black/40 text-white" data-testid="email-audience" />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Key points</FieldLabel>
        <Textarea value={i.key_points || ""} onChange={(e) => set("key_points", e.target.value)} rows={4} placeholder="3 short bullets the email should hit" className="border-white/10 bg-black/40 text-white" data-testid="email-key-points" />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Call to action</FieldLabel>
        <Input value={i.cta || ""} onChange={(e) => set("cta", e.target.value)} placeholder="e.g. book a 15-minute call" className="border-white/10 bg-black/40 text-white" data-testid="email-cta" />
      </div>
    </div>
  );
}

function AdForm({ i, set }) {
  return (
    <div className="space-y-3" data-testid="ad-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <FieldLabel>Platform</FieldLabel>
          <Select value={i.platform || "google"} onValueChange={(v) => set("platform", v)}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="ad-platform"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              <SelectItem value="google">Google</SelectItem>
              <SelectItem value="meta">Meta</SelectItem>
              <SelectItem value="linkedin">LinkedIn</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Tone</FieldLabel>
          <Select value={i.tone || "professional"} onValueChange={(v) => set("tone", v)}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="ad-tone"><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Product</FieldLabel>
        <Input value={i.product || ""} onChange={(e) => set("product", e.target.value)} placeholder="What are you advertising?" className="border-white/10 bg-black/40 text-white" data-testid="ad-product" />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Audience</FieldLabel>
        <Input value={i.audience || ""} onChange={(e) => set("audience", e.target.value)} placeholder="Who's it for?" className="border-white/10 bg-black/40 text-white" data-testid="ad-audience" />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>USP</FieldLabel>
        <Textarea value={i.usp || ""} onChange={(e) => set("usp", e.target.value)} rows={3} placeholder="What's the one thing that makes it stand out?" className="border-white/10 bg-black/40 text-white" data-testid="ad-usp" />
      </div>
    </div>
  );
}

// ---- OUTPUT PANE ----

const OutputPane = ({ outputRef, type, output, streaming, onCopy, onSaveTemplate, savedGenId, canSaveTemplate }) => {
  // Render output - simple markdown to display preserving line breaks
  return (
    <div className="flex h-full flex-col" data-testid="output-pane">
      <div className="flex items-center justify-between">
        <div className="text-eyebrow text-white/40">{LABEL[type]} · output</div>
        <div className="flex items-center gap-1">
          {output && !streaming && (
            <>
              <button onClick={onCopy} className="rounded-lg p-1.5 text-white/55 hover:bg-white/10 hover:text-white" title="Copy" data-testid="copy-output">
                <Copy className="h-3.5 w-3.5" />
              </button>
              <button onClick={onSaveTemplate} disabled={!canSaveTemplate} className="rounded-lg p-1.5 text-white/55 hover:bg-white/10 hover:text-white disabled:opacity-30" title="Save inputs as template" data-testid="save-template-btn">
                <BookmarkPlus className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
      <div
        ref={outputRef}
        className="mt-2 min-h-[420px] flex-1 overflow-y-auto rounded-xl border border-white/5 bg-black/40 p-4 text-sm text-white/85"
        data-testid="output-content"
      >
        {output ? (
          <MarkdownLite text={output} />
        ) : streaming ? (
          <div className="flex items-center gap-2 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /> Streaming…</div>
        ) : (
          <div className="flex h-full items-center justify-center text-white/30">Output will stream here</div>
        )}
        {streaming && output && <span className="ml-1 inline-block h-3 w-2 animate-pulse bg-fuchsia-400" />}
      </div>
      {savedGenId && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-300">
          <Save className="h-3 w-3" /> Saved to History
        </div>
      )}
    </div>
  );
};

function MarkdownLite({ text }) {
  // Minimal markdown for display: # / ## / ### headings, **bold**, lists, line breaks.
  const lines = text.split("\n");
  const out = [];
  let listBuffer = [];
  const flushList = (i) => {
    if (listBuffer.length === 0) return;
    out.push(<ul key={`ul-${i}`} className="my-2 list-disc space-y-1 pl-5">{listBuffer.map((l, idx) => <li key={idx}>{renderInline(l)}</li>)}</ul>);
    listBuffer = [];
  };
  lines.forEach((raw, idx) => {
    const line = raw;
    if (line.startsWith("# ")) { flushList(idx); out.push(<h1 key={idx} className="font-display mt-4 mb-2 text-2xl font-light text-white">{renderInline(line.slice(2))}</h1>); return; }
    if (line.startsWith("## ")) { flushList(idx); out.push(<h2 key={idx} className="font-display mt-4 mb-1.5 text-xl font-light text-white">{renderInline(line.slice(3))}</h2>); return; }
    if (line.startsWith("### ")) { flushList(idx); out.push(<h3 key={idx} className="font-display mt-3 mb-1 text-base font-medium text-white">{renderInline(line.slice(4))}</h3>); return; }
    if (line.startsWith("- ") || line.startsWith("* ")) { listBuffer.push(line.slice(2)); return; }
    flushList(idx);
    if (line.trim() === "") { out.push(<div key={idx} className="h-2" />); return; }
    out.push(<p key={idx} className="my-1.5 leading-relaxed">{renderInline(line)}</p>);
  });
  flushList(lines.length);
  return <div>{out}</div>;
}

function renderInline(s) {
  // Bold + code
  const parts = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\{\{[^}]+\}\})/g;
  let last = 0; let m;
  let key = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push(<span key={key++}>{s.slice(last, m.index)}</span>);
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={key++} className="text-white">{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) parts.push(<code key={key++} className="rounded bg-white/10 px-1 py-0.5 text-[12px] text-fuchsia-200">{tok.slice(1, -1)}</code>);
    else parts.push(<code key={key++} className="rounded bg-fuchsia-500/15 px-1 py-0.5 text-[12px] text-fuchsia-200">{tok}</code>);
    last = m.index + tok.length;
  }
  if (last < s.length) parts.push(<span key={key++}>{s.slice(last)}</span>);
  return parts;
}

// ============ HISTORY ============

function HistoryTab({ onReuse }) {
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterType === "all" ? "" : `?type=${filterType}`;
      const r = await api.get(`/content/generations${params}`);
      setItems(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this generation?")) return;
    try {
      await api.delete(`/content/generations/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      setOpenItem(null);
      toast.success("Deleted");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48 border-white/10 bg-black/40 text-white" data-testid="history-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="blog">Blog</SelectItem>
            <SelectItem value="linkedin">LinkedIn</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="ad">Ad</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-white/45">{items.length} items</div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center text-white/55" data-testid="history-empty">
          <History className="mx-auto h-7 w-7 text-white/30" />
          <div className="font-display mt-3 text-xl font-light">No history yet</div>
          <p className="mx-auto mt-2 max-w-md text-sm">Anything you generate gets saved automatically.</p>
        </div>
      ) : (
        <div className="glass overflow-hidden rounded-2xl" data-testid="history-list">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/55">
              <tr>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Model</th>
                <th className="px-4 py-2 text-right">Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.id} onClick={() => setOpenItem(g)} className="cursor-pointer border-t border-white/5 hover:bg-white/[0.03]" data-testid={`history-row-${g.id}`}>
                  <td className="px-4 py-3"><TypePill type={g.type} /></td>
                  <td className="truncate px-4 py-3 text-white">{g.title || LABEL[g.type]}</td>
                  <td className="px-4 py-3 text-white/55">{g.model || "—"}</td>
                  <td className="px-4 py-3 text-right text-white/45">{new Date(g.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={!!openItem} onOpenChange={(o) => !o && setOpenItem(null)}>
        <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto border-white/10 bg-[#0a0a0a] text-white" data-testid="history-drawer">
          {openItem && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl text-white">{openItem.title || LABEL[openItem.type]}</SheetTitle>
                <SheetDescription className="text-white/55">
                  <TypePill type={openItem.type} /> · {openItem.model} · {new Date(openItem.created_at).toLocaleString()}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 rounded-xl border border-white/5 bg-black/40 p-4 text-sm text-white/85">
                <MarkdownLite text={openItem.output_md || ""} />
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button onClick={() => navigator.clipboard.writeText(openItem.output_md || "").then(() => toast.success("Copied"))} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="drawer-copy">
                  <Copy className="mr-1.5 h-4 w-4" /> Copy
                </Button>
                <Button onClick={() => { onReuse(openItem); setOpenItem(null); }} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="drawer-reuse">
                  <RefreshCcw className="mr-1.5 h-4 w-4" /> Re-open in generator
                </Button>
                <Button onClick={() => remove(openItem.id)} variant="ghost" className="ml-auto text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="drawer-delete">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function TypePill({ type }) {
  const map = {
    blog: "border-indigo-500/30 bg-indigo-500/10 text-indigo-200",
    linkedin: "border-violet-500/30 bg-violet-500/10 text-violet-200",
    email: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-200",
    ad: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
  };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${map[type] || "border-white/20 bg-white/10 text-white"}`}>{LABEL[type] || type}</span>;
}

// ============ TEMPLATES ============

function TemplatesTab({ onUse }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/content/templates");
      setItems(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/content/templates/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Template deleted");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-white/45"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  }

  return items.length === 0 ? (
    <div className="glass rounded-2xl p-12 text-center text-white/55" data-testid="templates-empty">
      <BookOpen className="mx-auto h-7 w-7 text-white/30" />
      <div className="font-display mt-3 text-xl font-light">No templates yet</div>
      <p className="mx-auto mt-2 max-w-md text-sm">After generating something, click "Save inputs as template" to keep its inputs as a re-usable preset.</p>
    </div>
  ) : (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="templates-grid">
      {items.map((t) => (
        <div key={t.id} className="glass glass-hover group relative rounded-2xl p-5" data-testid={`template-card-${t.id}`}>
          <TypePill type={t.type} />
          <div className="font-display mt-3 text-lg font-light text-white">{t.name}</div>
          <div className="mt-1 line-clamp-2 text-xs text-white/45">{t.description || "—"}</div>
          {t.is_default && (
            <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-2 py-0.5 text-[10px] text-fuchsia-200">
              <Sparkles className="h-3 w-3" /> Default
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <Button onClick={() => onUse(t)} className="flex-1 rounded-xl bg-white text-black hover:bg-zinc-200" data-testid={`template-use-${t.id}`}>
              Use template
            </Button>
            {!t.is_default && (
              <button onClick={() => remove(t.id)} className="rounded-xl p-2 text-white/40 hover:bg-red-500/10 hover:text-red-300" data-testid={`template-delete-${t.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ SAVE TEMPLATE DIALOG ============

function SaveTemplateDialog({ open, onOpenChange, type, inputs }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) { setName(""); setDescription(""); } }, [open]);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/content/templates", { type, name: name.trim(), description: description.trim() || null, inputs });
      toast.success("Template saved");
      onOpenChange(false);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white" data-testid="save-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Save as template</DialogTitle>
          <DialogDescription className="text-white/55">
            Saves the current inputs as a re-usable preset for "{LABEL[type]}".
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5">
            <FieldLabel>Name</FieldLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Q1 thought leadership" className="border-white/10 bg-black/40 text-white" data-testid="template-name" />
          </div>
          <div className="space-y-1.5">
            <FieldLabel>Description (optional)</FieldLabel>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="border-white/10 bg-black/40 text-white" data-testid="template-description" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/65 hover:text-white">Cancel</Button>
            <Button type="submit" disabled={saving || !name.trim()} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="template-save">
              {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Save template
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
